import { useEffect } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './lib/auth';
import { useTheme } from './lib/theme';
import { Spinner } from './components/kit/Spinner';
import { AppShell } from './components/app/AppShell';
import { startSyncScheduler } from './lib/sync';

import { PhoneRoute } from './routes/auth/PhoneRoute';
import { OtpRoute } from './routes/auth/OtpRoute';
import { InviteRoute } from './routes/auth/InviteRoute';
import { SignupShell } from './routes/auth/signup/SignupShell';
import { HomeRoute } from './routes/home/HomeRoute';
import { TodayRoute } from './routes/today/TodayRoute';
import { TripDetailRoute } from './routes/trips/TripDetailRoute';
import { HistoryRoute } from './routes/trips/HistoryRoute';
import { DutySlipViewRoute } from './routes/trips/DutySlipViewRoute';
import { VehicleRoute } from './routes/vehicle/VehicleRoute';
import { VehicleDocViewerRoute } from './routes/vehicle/VehicleDocViewerRoute';
import { ProfileRoute } from './routes/profile/ProfileRoute';
import { LanguageRoute } from './routes/profile/LanguageRoute';
import { KycIndexRoute } from './routes/profile/KycIndexRoute';
import { KycDlEditRoute } from './routes/profile/KycDlEditRoute';
import { KycAadhaarEditRoute } from './routes/profile/KycAadhaarEditRoute';
import { KycPanEditRoute } from './routes/profile/KycPanEditRoute';
import { DocViewerRoute } from './routes/profile/DocViewerRoute';
import { HelpRoute } from './routes/help/HelpRoute';
import { NotificationsRoute } from './routes/notifications/NotificationsRoute';
import { NotFoundRoute } from './routes/NotFoundRoute';
import { requestPersistentStorage } from './lib/idb';

function Splash() {
  return (
    <div className="flex items-center justify-center h-full">
      <Spinner size={32} />
    </div>
  );
}

function AuthGate({ children }: { children: React.ReactNode }) {
  const status = useAuth(s => s.status);
  const location = useLocation();
  const nav = useNavigate();
  useEffect(() => {
    if (status === 'unauthenticated' && !location.pathname.startsWith('/auth')) {
      nav('/auth/phone', { replace: true });
    }
  }, [status, location.pathname, nav]);
  if (status === 'loading') return <Splash />;
  return <>{children}</>;
}

export default function App() {
  const loadMe = useAuth(s => s.loadMe);
  const initTheme = useTheme(s => s.init);
  useEffect(() => {
    void loadMe();
    void requestPersistentStorage();
    initTheme();
    startSyncScheduler();
  }, [loadMe, initTheme]);

  return (
    <AuthGate>
      <Routes>
        {/* Auth screens have no chrome */}
        <Route path="/auth/phone" element={<PhoneRoute />} />
        <Route path="/auth/otp" element={<OtpRoute />} />
        <Route path="/auth/invite" element={<InviteRoute />} />
        <Route path="/auth/signup" element={<SignupShell />} />

        {/* Everything else wrapped by AppShell (header + drawer + sync banner) */}
        <Route element={<AppShell />}>
          <Route path="/" element={<HomeRoute />} />
          <Route path="/today" element={<TodayRoute />} />
          <Route path="/trips/history" element={<HistoryRoute />} />
          <Route path="/trips/:id" element={<TripDetailRoute />} />
          <Route path="/duty-slip-view/:id" element={<DutySlipViewRoute />} />
          <Route path="/vehicle" element={<VehicleRoute />} />
          <Route path="/vehicle/compliance/:type/view" element={<VehicleDocViewerRoute />} />
          <Route path="/profile" element={<ProfileRoute />} />
          <Route path="/profile/language" element={<LanguageRoute />} />
          <Route path="/profile/kyc" element={<KycIndexRoute />} />
          <Route path="/profile/kyc/dl" element={<KycDlEditRoute />} />
          <Route path="/profile/kyc/aadhaar" element={<KycAadhaarEditRoute />} />
          <Route path="/profile/kyc/pan" element={<KycPanEditRoute />} />
          <Route path="/profile/kyc/:doc/view" element={<DocViewerRoute />} />
          <Route path="/help" element={<HelpRoute />} />
          <Route path="/notifications" element={<NotificationsRoute />} />
          <Route path="*" element={<NotFoundRoute />} />
        </Route>
      </Routes>
    </AuthGate>
  );
}
